/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/bravada-material-slots.ts
 * FILE_ID: QB-AGGRID-MATERIAL-SLOTS-001
 * PURPOSE: Pure I→O resolvers that pluck slot-specific material values
 *          off a QuoteLineItem.materials[] array for AG-Grid columns.
 *          The four slot ids (primer / basecoat / topcoat / accessories)
 *          mirror the backend LineItemMaterial.SLOT_CHOICES so the FE
 *          can render the four sub-bands the Excel sheet shows.
 *
 * WHY: Bravada's Scope & Pricing master template breaks the PRODUCTS
 *      band into four slot columns (Excel cols AB-AL):
 *
 *        AB AC AD │ AE AF AG │ AH AI AJ │ AK AL
 *        ─ Primer ─│─ Basecoat ─│─ Topcoat ─│─ Access. ─
 *
 *      Estimators expect to type a primer's unit cost into the Primer
 *      slot, basecoat's into Basecoat, etc. — NOT a single aggregate
 *      cost column. This module powers the four sub-bands in
 *      bravada-product-slot-bands.ts.
 *
 * FLOW:
 *   ┌─────────────────────┐   ┌───────────────┐   ┌──────────────────┐
 *   │ QuoteLineItem +     │ → │ findMaterial  │ → │ Material slot or │
 *   │ slot id ('primer')  │   │ BySlot()      │   │ null             │
 *   └─────────────────────┘   └───────────────┘   └──────────────────┘
 *   ┌─────────────────────┐   ┌───────────────┐   ┌──────────────────┐
 *   │ Field id            │ → │ getSlot       │ → │ unit_cost /      │
 *   │ ('primer_unit_cost')│   │ FieldValue()  │   │ coverage / total │
 *   └─────────────────────┘   └───────────────┘   └──────────────────┘
 *
 * RELATED:
 *   - QB-AGGRID-BRAVADA-PRICING-001 (bravada-pricing-bands.ts) —
 *     consumer of these resolvers via the slot-band schema.
 *   - QB-AGGRID-FMT-001 (scope-ag-grid-formatters.ts) — getComputedValue
 *     delegates slot-prefixed field ids to getSlotFieldValue here.
 *   - LineItemMaterial backend model: quotebuilder/models/line_item.py
 *     L572-581 (SLOT_CHOICES — the source of truth).
 */
import type { LineItemMaterial, QuoteLineItem } from '@/lib/quoteApi'

/** Slot ids in Excel left-to-right order — see file docblock. */
export type MaterialSlot = 'primer' | 'basecoat' | 'topcoat' | 'other'

/** All four slots in their Excel display order. Used by tests + by
 *  the band schema to keep the column order stable. */
export const MATERIAL_SLOTS: ReadonlyArray<MaterialSlot> = [
  'primer',
  'basecoat',
  'topcoat',
  'other',
] as const

/** Human-readable label per slot. 'other' renders as 'Accessories'
 *  because that's the Excel header text. */
export const SLOT_LABELS: Readonly<Record<MaterialSlot, string>> = {
  primer: 'Primer',
  basecoat: 'Basecoat',
  topcoat: 'Topcoat',
  other: 'Accessories',
} as const

/**
 * Find the first material in `data.materials` matching the given slot.
 *
 * Returns null when:
 *   • data is null/undefined
 *   • data.materials is missing or empty
 *   • no material in the array has the requested slot
 *
 * If multiple materials share the same slot (legacy data drift), the
 * one with the lowest sort_order wins via the array's natural order
 * (the backend always returns materials ordered by sort_order).
 */
export function findMaterialBySlot(
  data: QuoteLineItem | null | undefined,
  slot: MaterialSlot,
): LineItemMaterial | null {
  if (!data || !Array.isArray(data.materials)) return null
  for (const m of data.materials) {
    if (m.slot === slot) return m
  }
  return null
}

/**
 * Pure resolver for a slot-prefixed field id (e.g. 'primer_unit_cost')
 * → the slot-specific value off `data.materials[slot=primer]`.
 *
 * Recognised field-id suffixes:
 *   - `_unit_cost`      → material.unit_cost              (Excel col 1)
 *   - `_coverage`       → material.coverage_per_unit       (Excel col 2)
 *   - `_total_cost`     → material.cost_per_m2             (Excel col 3)
 *
 * Accessories has a slightly different shape: no `coverage` column,
 * `_total_cost` reads off `quantity * unit_cost` when cost_per_m2 is
 * absent (per the Bravada Excel where AK = unit cost, AL = total $).
 *
 * Returns null when the slot is empty or the field is unrecognised.
 * Null returns render as blank cells (matches Excel behaviour for
 * unpopulated material slots).
 */
export function getSlotFieldValue(
  data: QuoteLineItem | null | undefined,
  fieldId: string,
): number | null {
  const parsed = parseSlotFieldId(fieldId)
  if (!parsed) return null
  const { slot, suffix } = parsed
  const material = findMaterialBySlot(data, slot)
  if (!material) return null

  switch (suffix) {
    case 'unit_cost':
      return numeric(material.unit_cost)
    case 'coverage':
      return numeric(material.coverage_per_unit)
    case 'total_cost': {
      // Excel "Total Cost $'s" semantics:
      //   • Primer/Basecoat/Topcoat: shown as $/m² (= unit_cost / coverage).
      //     The backend persists this as `cost_per_m2`.
      //   • Accessories: shown as the line-total $ (= unit_cost × qty),
      //     reflecting Bravada's per-piece accessory pricing.
      const stored = numeric(material.cost_per_m2)
      if (stored !== null && stored !== 0) return stored
      // Derive when absent for the accessories shape.
      const unit = numeric(material.unit_cost)
      const qty = numeric(material.quantity)
      if (unit !== null && qty !== null) return unit * qty
      return null
    }
    default:
      return null
  }
}

/** Parse a slot-prefixed field id into its `(slot, suffix)` parts.
 *  Returns null when the id doesn't start with a recognised slot. */
export function parseSlotFieldId(
  fieldId: string,
): { slot: MaterialSlot; suffix: string } | null {
  for (const slot of MATERIAL_SLOTS) {
    const prefix = slot === 'other' ? 'accessories_' : `${slot}_`
    if (fieldId.startsWith(prefix)) {
      return { slot, suffix: fieldId.slice(prefix.length) }
    }
  }
  return null
}

/** Tight numeric coercion: returns null (not 0) for blank/garbage
 *  inputs so cells stay empty when the slot has no value. */
function numeric(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const n = Number.parseFloat(String(v))
  return Number.isFinite(n) ? n : null
}
